import { DEFAULT_SETTINGS } from '@edh/shared';

/**
 * What the app calls itself, and what this group calls itself.
 *
 * The app name is a constant. The guild name is not — leaders can change it, so
 * it lives in league settings and the live value comes from the store.
 *
 * The sign-in screen is the exception: it renders before any league data has
 * loaded, and cannot ask the server what the guild is called without a token it
 * does not yet have. So the name is cached on every load and read back from
 * there, falling back to the default on a device that has never signed in.
 *
 * All of this is deliberately separate from the infrastructure names. The
 * Cognito hosted domain, the user pool, the bucket and the repository are still
 * "edh-misadventures" and stay that way: the domain string is part of the
 * redirect URI registered with Google, and renaming it would break sign-in for
 * everyone to change something nobody sees.
 */

export const APP_NAME = 'EDH Guild';

const NAME_KEY = 'edh.guildName';
const ICON_KEY = 'edh.guildIcon';

export const DEFAULT_GUILD_NAME = DEFAULT_SETTINGS.guildName;

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/** For screens that render before the league is available. */
export const cachedGuildName = (): string => read(NAME_KEY) || DEFAULT_GUILD_NAME;
export const cachedGuildIcon = (): string | null => read(ICON_KEY);

export function cacheGuildIdentity(name: string, icon: string | null): void {
  try {
    if (name) localStorage.setItem(NAME_KEY, name);
    if (icon) localStorage.setItem(ICON_KEY, icon);
    else localStorage.removeItem(ICON_KEY);
  } catch {
    // Private browsing or a full quota; the defaults are a fine answer.
  }
}
